// @greenlight-spec: 28-it-dashboard-ui.md#integrations--integrations-and-grants
// @greenlight-spec: 09-data-brokering.md#scope-model
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: decisions/0039-injected-grant-is-disclosure.md
import * as React from 'react';
import { ExternalLink, ShieldAlert } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { DOCS_LINKS } from '@/lib/docsLinks';

export interface DataAccessTermsPanelProps {
  /** The upstream's display name, or null when the registration has not named one yet. */
  providerName: string | null;
  /** Injected delivery hands the holder the key itself, so no call is brokered. */
  injected: boolean;
  /**
   * User passthrough acts as each person who connects, including while they are
   * away. The grant is an ordinary integration grant; there is no second setting.
   */
  userPassthrough?: boolean;
  /**
   * Org-wide delegation (Box): one app credential acts as any user in the
   * enterprise, named on each call by the signed-in person's email. Nobody
   * connects, and only a call with someone signed in can run.
   */
  orgWideDelegation?: boolean;
  /** Present while the org has not accepted the terms: renders the acceptance checkbox. */
  acceptance?: { accepted: boolean; onAcceptedChange: (accepted: boolean) => void };
}

/**
 * What granting an integration credential means, stated on every registration.
 * Until the org accepts the data-access terms it also carries the acceptance.
 */
export function DataAccessTermsPanel({
  providerName,
  injected,
  userPassthrough = false,
  orgWideDelegation = false,
  acceptance,
}: DataAccessTermsPanelProps): React.JSX.Element {
  const upstream = providerName ?? 'the provider';
  return (
    <Alert className="border-border bg-muted/30" data-testid="data-access-terms-panel">
      <ShieldAlert aria-hidden="true" />
      <AlertTitle>What granting this credential means</AlertTitle>
      <AlertDescription className="gap-2 text-[12.5px]">
        {orgWideDelegation ? (
          <>
            <p data-testid="data-access-terms-delegation">
              This integration acts as each signed-in person, not as a shared account. An app you
              grant it to calls {upstream} as the person using it, matched by their email, and{' '}
              {upstream} applies that person&apos;s own permissions. Every call is audited as that
              person.
            </p>
            <p>
              The credential you register can act as any managed user in your {upstream} enterprise,
              so treat its secret as enterprise-wide access. Nobody connects an account, and a call
              with no one signed in is refused, so scheduled or background work cannot use the
              grant.
            </p>
          </>
        ) : userPassthrough ? (
          <>
            <p data-testid="data-access-terms-passthrough">
              This is a user passthrough integration, not a shared key. An app you grant it to calls{' '}
              {upstream} as each person who connects their account, and {upstream} applies that
              person&apos;s own permissions. Every call is audited as that person.
            </p>
            <p>
              Granting it is the same approval you give any other integration. That approval lets
              the app act for those people while they are away — a report or a sync they set up — as
              well as while they are in the app. There is no separate setting, and each person is
              not asked again: they agreed when they connected at {upstream}.
            </p>
            <p>
              The upstream client you register limits which permissions the app can ask for. Each
              person&apos;s own access in {upstream} limits what a call as them can do.
            </p>
          </>
        ) : (
          <p>
            Granting this credential to an app or a person lets their coding agent call {upstream}{' '}
            with everything this key can do.
          </p>
        )}
        {userPassthrough || orgWideDelegation ? null : injected ? (
          <p data-testid="data-access-terms-injected">
            Because delivery is injected, the holder receives the key itself (see the warning
            above), so Greenlight records who was granted it but cannot broker or audit the calls
            made with it.
          </p>
        ) : (
          <p>
            Greenlight brokers and audits every call, but it does not narrow what the key can do.
            The key&apos;s own permissions in {upstream} are the limit.
          </p>
        )}
        {userPassthrough || orgWideDelegation ? null : (
          <p>
            Scope the key to the least the work needs, on a dedicated account in {upstream} where it
            allows one.
          </p>
        )}
        <a
          href={DOCS_LINKS.dataAccessTerms}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11.5px] font-medium text-link hover:underline"
        >
          Read the data-access terms
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </a>
        {acceptance && (
          <div className="flex items-start gap-2 pt-1">
            <Checkbox
              id="accept-data-access-terms"
              checked={acceptance.accepted}
              onCheckedChange={(v) => acceptance.onAcceptedChange(v === true)}
              className="mt-0.5"
              data-testid="accept-data-access-terms"
            />
            <Label
              htmlFor="accept-data-access-terms"
              className="cursor-pointer text-[12.5px] font-normal leading-snug text-foreground"
            >
              I accept Greenlight&apos;s data-access terms for this organization. This is asked once
              per organization.
            </Label>
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}
